import { Button, ButtonMode, ButtonSize } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useContext, useMemo, useState } from 'react';
import { ToastContext } from '../../context/ToastContext';
import { useSendFeedback } from '../../hooks/chat/useSendFeedback';
import { useTrackUsage } from '../../hooks/usage/track';
import { SendFeedbackRequest } from '../../services/chat/feedback.service';
import { Question } from '../../services/chat/getSessions.service';
import styles from './FeedbackForm.module.scss';

const cx = classNames.bind(styles);

interface FeedbackFormProps {
  sessionId: string;
  historyItem: Question;
}

// const feedbackTypes = {
//   INCORRECT_RESPONSE: ['Incorrect response', 'Select if response is non-factual or irrelevant'],
//   INCORRECT_SOURCES: ['Incorrect sources', 'Select if sources are incorrect'],
//   // REQUEST_NEW_FEATURE: 'Request new feature',
// };

const FeedbackForm = ({ sessionId, historyItem }: FeedbackFormProps): React.ReactElement => {
  const [selectedFeedbackTypes /* , setSelectedFeedbackTypes */] = useState<string[]>([]);
  const [comment /* , setComment */] = useState('');
  const [thumbsUpFeedback, setThumbsUpFeedback] = useState<
    Required<SendFeedbackRequest['feedback_body']>['user_thumbsup_feedback']
  >([]);
  const [thumbsDownFeedback, setThumbsDownFeedback] = useState<
    Required<SendFeedbackRequest['feedback_body']>['user_thumbsdown_feedback']
  >({
    correct_response: '',
    correct_sources: '',
  });
  const { success } = useContext(ToastContext);
  const { mutate, isLoading } = useSendFeedback(() => {
    success('Your feedback has been sent. Thank you for helping us improve!');
  });
  const trackUsage = useTrackUsage();

  // const handleToggleSelectedFeedbackType = (option: string) => {
  //   const nextSelectedFeedbackTypes = [...selectedFeedbackTypes];
  //   const index = nextSelectedFeedbackTypes.indexOf(option);

  //   if (index >= 0) {
  //     nextSelectedFeedbackTypes.splice(index, 1);
  //   } else {
  //     nextSelectedFeedbackTypes.push(option);
  //   }

  //   setSelectedFeedbackTypes(nextSelectedFeedbackTypes);
  // };

  const handleChangeThumbsUpFeedback = (doclink: string, value: boolean) => {
    const nextThumbsUpFeedback = thumbsUpFeedback.filter((item) => item.doclink !== doclink);
    nextThumbsUpFeedback.push({ doclink, user_thumbs_up: value });

    setThumbsUpFeedback(nextThumbsUpFeedback);
  };

  const handleSubmit = () => {
    const comments: string[] = [];

    if (selectedFeedbackTypes.length > 0) {
      comments.push(selectedFeedbackTypes.join(','));
    }

    if (comment.trim()) {
      comments.push(comment.trim());
    }

    if (readyToSubmit) {
      trackUsage.submitFeedback({
        sessionId,
        seqId: historyItem.query_seq_id,
        thumbsUp: !!historyItem.usr_thumbs_up,
      });

      mutate({
        sessionId,
        seqId: historyItem.query_seq_id,
        feedback_body: {
          user_thumbs_up: !!historyItem.usr_thumbs_up,
          user_thumbsdown_feedback: !historyItem.usr_thumbs_up ? thumbsDownFeedback : undefined,
          user_thumbsup_feedback: historyItem.usr_thumbs_up ? thumbsUpFeedback : undefined,
        },
        user_feedback_type: 'local',
        comments: comments.join(' | '),
      });
    }
  };

  const sourceDocumentsUniq = useMemo(
    () =>
      historyItem.source_documents?.reduce((acc: Required<Question>['source_documents'], item) => {
        if (!acc.some((accItem) => accItem.url === item.url)) {
          return [...acc, item];
        }

        return acc;
      }, []) ?? [],
    [historyItem]
  );

  const readyToSubmit = useMemo(() => {
    if (historyItem.usr_thumbs_up) {
      if (thumbsUpFeedback.length === sourceDocumentsUniq.length) {
        return true;
      }
    } else if (thumbsDownFeedback.correct_response && thumbsDownFeedback.correct_sources) {
      return true;
    }

    return false;
  }, [historyItem, sourceDocumentsUniq, thumbsUpFeedback, thumbsDownFeedback]);

  return (
    <>
      Thanks, your feedback helps improve future responses.
      <div className={cx('body')} data-csgenie-feedback-actions="0">
        {historyItem.usr_thumbs_up ? (
          <>
            <div>Please let us know which sources were relevant.</div>
            {sourceDocumentsUniq.map((doc) => (
              <div className={cx('sourceLine')} key={doc.url}>
                <div className={cx('sourceLineTitle')}>{doc.title}</div>
                <div className={cx('sourceLineBtns')}>
                  <Button
                    className={cx('optionsButton', {
                      optionsButtonActive:
                        thumbsUpFeedback.find((item) => item.doclink === doc.url)?.user_thumbs_up === true,
                    })}
                    size={ButtonSize.SMALL}
                    onClick={() => handleChangeThumbsUpFeedback(doc.url, true)}
                  >
                    Yes
                  </Button>
                  <div className={cx('sourceLineBtnsOr')} />
                  <Button
                    className={cx('optionsButton', {
                      optionsButtonActive:
                        thumbsUpFeedback.find((item) => item.doclink === doc.url)?.user_thumbs_up === false,
                    })}
                    size={ButtonSize.SMALL}
                    onClick={() => handleChangeThumbsUpFeedback(doc.url, false)}
                  >
                    No
                  </Button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div>Please enter correct information so that we can improve the model.</div>
            <div className={cx('comment')}>
              <div>Correct Response</div>
              <textarea
                value={thumbsDownFeedback.correct_response}
                onChange={(e) => setThumbsDownFeedback((prev) => ({ ...prev, correct_response: e.target.value }))}
                disabled={isLoading}
                placeholder="Please enter the expected response"
              />
            </div>
            <div className={cx('comment')}>
              <div>Correct Source</div>
              <textarea
                value={thumbsDownFeedback.correct_sources}
                onChange={(e) => setThumbsDownFeedback((prev) => ({ ...prev, correct_sources: e.target.value }))}
                placeholder="Please enter the URL of the expected source"
                disabled={isLoading}
              />
            </div>
            {/*
            <div>Please let us know what went wrong.</div>
            <div className={cx('options')}>
              {Object.entries(feedbackTypes).map(([id, texts]) => (
                <Tooltip
                  content={texts[1]}
                  trigger={
                    <Button
                      key={id}
                      className={cx('optionsButton', { optionsButtonActive: selectedFeedbackTypes.includes(id) })}
                      onClick={() => handleToggleSelectedFeedbackType(id)}
                      disabled={isLoading}
                    >
                      {texts[0]}
                    </Button>
                  }
                />
              ))}
            </div>
            <div className={cx('comment')}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading}
                placeholder="Additional Comments (optional)"
              />
            </div>
            */}
          </>
        )}
        <div className={cx('submit')}>
          <Button mode={ButtonMode.SECONDARY} onClick={handleSubmit} disabled={!readyToSubmit || isLoading}>
            Send Feedback
          </Button>
        </div>
      </div>
    </>
  );
};

export default FeedbackForm;
